import { RootState } from '@/store/rootReducer';
import { logout } from '@/store/slices/authSlice';
import { store } from '@/store/store';
import { ApiRequestOptions } from '@/types/ApiRequestOptions';
import { ApiResponse } from '../types/ApiResponse';
import { AppConstants } from '@/app.constants';
import {
  normalizeAppReturnPath,
  POST_LOGIN_REDIRECT_STORAGE_KEY,
} from '@/lib/auth/post-login-redirect';
import { ROUTES } from '@/route/routes.constants';
import { getInspectorTimeZone } from '@/utils/inspectorTimeZone.util';
import { isInspectionInProgress } from '@/utils/isInspectionInProgress.util';
import {
  extractHttpResponseDiagnostics,
  recordHttpResponseDiagnostics,
} from '@/lib/httpResponseDiagnostics';

export async function clivApiRequest<T>(
  { method = 'GET', url, params, body, headers = {}, signal }: ApiRequestOptions,
  skipAuth = false
): Promise<ApiResponse<T>> {
  try {
    const urlObj = new URL(url);
    const token = (store.getState() as RootState).auth.token;

    // Append query parameters for GET requests
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        urlObj.searchParams.append(key, String(value));
      });
    }
    const authToken = `Bearer ${token}`;
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        [AppConstants.X_INSPECTOR_TIMEZONE_HEADER]: getInspectorTimeZone(),
        ...headers,
      },
      ...(signal && { signal }),
      credentials: 'include',
    };
    if (!skipAuth) {
      options.headers = { ...options.headers, Authorization: authToken };
    }
    // Add body for methods other than GET
    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(urlObj.toString(), options);

    const diagnostics = extractHttpResponseDiagnostics({
      method,
      url: urlObj.toString(),
      status: response.status,
      headers: response.headers,
    });
    recordHttpResponseDiagnostics(diagnostics);

    let responseData = null;
    if (response.status === 401 && !skipAuth && token) {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      if (returnPath && returnPath !== ROUTES.ROOT) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, normalizeAppReturnPath(returnPath));
      }
      if (!isInspectionInProgress(window.location.pathname)) {
        store.dispatch(logout());
      }
    }
    if (response.status !== 204) {
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        responseData = await response.json();
      }
    }

    return {
      requestId: diagnostics.requestId,
      data: responseData,
      status: response.status,
      ok: response.ok,
    };
  } catch (error: any) {
    return {
      requestId: '',
      data: null,
      status: 500,
      ok: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}
